import React, { useEffect, useState } from "react";
import "../../sharedPages/Trading/Trading.css";
import DatePicker, { CalendarContainer } from "react-datepicker";
import InfoPopup from "../../sharedPages/CommonComponents/InfoPopup/InfoPopup.jsx";
import CustomSwitch from "../../sharedPages/CommonComponents/CustomSwitch/CustomSwitch.jsx";
import BubbleSvgButton from "../../sharedPages/CommonComponents/BubbleSvgButton/BubbleSvgButton";
import { useSelector, useDispatch } from "react-redux";
import useApi from "../../../helpers/apiHelper/requestHelper";
import { toastify } from "../../../helpers/toast/toastify.js";
import { setRefetchSpecificUser } from "../../../redux/multicommunity/multicommunity";

const DateSelector = ({ selectedDate, setSelectedDate }) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const Container = ({ children, className }) => {
    return (
      <div className="calendarContainer">
        <CalendarContainer className={`w-100 ${className}`}>
          <div style={{ width: "100%", position: "relative" }}>{children}</div>
        </CalendarContainer>
      </div>
    );
  };

  return (
    <div className="mamberDateSelectBox">
      <DatePicker
        calendarContainer={Container}
        className="form-control memberDropDown"
        icon={
          <svg
            height="5.083"
            viewBox="555.521 760 9.149 5.083"
            width="9.149"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M560.095 765.083 555.521 760h9.149l-4.575 5.083Z"
              data-name="Triangle"
              fill-rule="evenodd"
            />
          </svg>
        }
        onChange={(date) => setSelectedDate(date)}
        placeholderText="Select end date"
        selected={selectedDate}
        showIcon
      />
    </div>
  );
};

const TradeCalender = () => {
  const { user } = useSelector((state) => state.user);
  const community = useSelector((state) => state?.user?.user?.leaderCommunity);
  const dispatch = useDispatch();
  const [endDate, setEndDate] = useState(null);
  const [originalDate, setOriginalDate] = useState(null);
  const [isdetail, setIsdetail] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [optimisticEmailReport, setOptimisticEmailReport] = useState(null);
  const [optimisticTraderEmailReport, setOptimisticTraderEmailReport] = useState(null);
  const api = useApi();
  const [stats, setStats] = useState({});
  const getUserStats = async () => {
    const response = await api("post", "multiCommunity/getSpecificUserStats", {
      userIds: [user?._id],
    });
    if (response?.status) setStats(response?.data);
  };

  useEffect(() => {
    if (user) getUserStats();
  }, [user]);

  useEffect(() => {
    if (community?.statsFrom) {
      const communityDate = new Date(community.statsFrom);
  
      // Subtract 7 days
      const sevenDaysEarlier = new Date(communityDate);
      sevenDaysEarlier.setDate(communityDate.getDate() - 7);
  
      setEndDate(sevenDaysEarlier);
      setOriginalDate(sevenDaysEarlier);
    }
  }, [community]);
  
  // useEffect(() => {
  //   if (community) {
  //     const communityDate = new Date(community?.statsFrom);
  //     setEndDate(communityDate);
  //     setOriginalDate(communityDate);
  //   }
  // }, [community]);

  // Reset optimistic state when user data changes to match optimistic state (after successful API call)
  useEffect(() => {
    if (
      optimisticEmailReport !== null &&
      user?.leaderCommunity?.receiveEmailReport === optimisticEmailReport
    ) {
      setOptimisticEmailReport(null);
    }
  }, [user?.leaderCommunity?.receiveEmailReport, optimisticEmailReport]);

  // Reset optimistic state for trader email report when user data changes to match optimistic state
  useEffect(() => {
    if (
      optimisticTraderEmailReport !== null &&
      user?.getEmailReport === optimisticTraderEmailReport
    ) {
      setOptimisticTraderEmailReport(null);
    }
  }, [user?.getEmailReport, optimisticTraderEmailReport]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 576);
    };

    // Check the screen size on component mount
    handleResize();

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getUtcMidnightISOString = (date) => {
    const utcDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    return utcDate.toISOString();
  };

  // Helper function to check if date has changed
  const hasDateChanged = () => {
    if (!endDate || !originalDate) return false;
    return endDate.getTime() !== originalDate.getTime();
  };

  const changeStatsDate = async () => {
    const response = await api("post", "community/update", {
      statsFrom: getUtcMidnightISOString(endDate),
      id: community?._id,
    });
    if (response?.status) {
      dispatch(setRefetchSpecificUser(true));
      await getUserStats();
      setOriginalDate(new Date(endDate));
      toastify("Date updated successfully.", "success");
    } else {
      toastify(response?.message || "Unable to change date.");
    }
  };
    const traderMonthlyReport = async () => {
      // Get current displayed state (optimistic or actual)
      const currentDisplayedState = optimisticTraderEmailReport !== null 
        ? optimisticTraderEmailReport 
        : user?.getEmailReport;
      const newEmailReportState = !currentDisplayedState;
      
      // Optimistic UI update - change immediately
      setOptimisticTraderEmailReport(newEmailReportState);
       
      try {
        const response = await api("post", "users/update-email-report", {
          id: user?._id,
          getEmailReport: newEmailReportState,
        });
        
        if (response?.status) {
          dispatch(setRefetchSpecificUser(true));
          setTimeout(() => {
            toastify("Email preference set successfully!", "success");
          }, 2000);
        } else {
          // Revert to previous displayed state on error
          setOptimisticTraderEmailReport(currentDisplayedState);
          toastify(response?.message || "Failed to update email preference");
        }
      } catch (error) {
        // Revert to previous displayed state on error
        setOptimisticTraderEmailReport(currentDisplayedState);
        toastify("Failed to update email preference");
      }
    };

  const monthlyReport = async () => {
    // Get current displayed state (optimistic or actual)
    const currentDisplayedState = optimisticEmailReport !== null
      ? optimisticEmailReport
      : user?.leaderCommunity?.receiveEmailReport;
    const newEmailReportState = !currentDisplayedState;

    // Optimistic UI update - change immediately
    setOptimisticEmailReport(newEmailReportState);

    try {
      const response = await api("post", "community/update", {
        id: user?.leaderCommunity?._id,
        receiveEmailReport: newEmailReportState,
      });

      if (response?.status) {
        dispatch(setRefetchSpecificUser(true));
        setTimeout(() => {
          toastify("Email preference set successfully!", "success");
        }, 2000);
      } else {
        // Revert to previous displayed state on error
        setOptimisticEmailReport(currentDisplayedState);
        toastify(response?.message || "Failed to update email preference");
      }
    } catch (error) {
      // Revert to previous displayed state on error
      setOptimisticEmailReport(currentDisplayedState);
      toastify("Failed to update email preference");
    }
  };

  return (
    <div className="calculatorTradePerformance">
      {/* <div className="container-fluid g-0"> */}
        <div className="tableBoxWrap">
              <div className="separatorFlexWrap">
                <div className="separatorTrade">
                  <div className="separatorLine"></div>
                </div>
              </div>
              <div className="rightSideBarWrap">
                {/* <div className="accountPerfomance">
                  <p className="title mb-0">
                    <span className="pe-2 d-sm-inline d-none">
                      Calculate your trading account performance from
                    </span>
                    <span className="pe-2 d-sm-none d-inline">
                      Calculate your trading account performance from
                    </span>
                    <InfoPopup
                      contentItems={[
                        { text: "Invite VIP Members", type: "heading" },
                        {
                          text: "Turn this on to create a VIP subscription for your current lifetime(or just a longer access period) members at our cost price of $15/ month. If you define an end date for the VIP member then member will be offered to resubscribe at end of VIP membership. You can turn it off once you’ve onboarded all your VIP members without affecting their subscription.",
                          type: "paragraph",
                        },
                      ]}
                    />
                  </p>
                  <div className="SwithTitle mt-3 d-sm-none d-block">
                    <CustomSwitch
                      checked={isdetail}
                      onChange={() => {
                        setIsdetail(!isdetail);
                      }}
                    />
                    <span className="switchText ps-3">See details</span>
                  </div>
                  {(!isMobile || isdetail) && (
                    <>
                      <DateSelector
                        className="VipMemberDropdown"
                        selectedDate={endDate}
                        setSelectedDate={setEndDate}
                      />
                      {hasDateChanged() && (
                        <div className="mt-2">
                          <BubbleSvgButton
                            text={"Save"}
                            type="button"
                            variant="black"
                            onClick={changeStatsDate}
                            mainDivStyle={{
                              zIndex: "0",
                            }}
                            containerStyling={{
                              justifyContent: "center",
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div> */}
                <div className="reportWrap">
                  <div className="SwithTitle">
                    <CustomSwitch
                      checked={
                        user?.leaderCommunity
                          ? optimisticEmailReport !== null
                            ? optimisticEmailReport
                            : user?.leaderCommunity?.receiveEmailReport
                          : optimisticTraderEmailReport !== null
                            ? optimisticTraderEmailReport
                            : user?.getEmailReport
                      }
                      onChange={() => {
                        if (user?.leaderCommunity) {
                          monthlyReport();
                          return;
                        }
                        traderMonthlyReport();
                      }}
                    />
                    <span className="switchText ps-3">
                      monthly report by email
                    </span>
                  </div>
                </div>
              </div>
           
        </div>
      {/* </div> */}
    </div>
  );
};

export default TradeCalender;

